/**
 * SEOHead Component
 *
 * Comprehensive SEO meta tags component using direct DOM head management.
 * Provides title, description, Open Graph, Twitter Cards, canonical URLs, and more.
 *
 * Note: react-helmet-async is incompatible with React 19 (client-side injection broken).
 * This component uses useEffect-based DOM manipulation instead.
 *
 * @example
 * <SEOHead
 *   title="Best Wireless Thermometers 2025"
 *   description="Compare the top wireless thermometers..."
 *   canonicalUrl="https://backyahdbbq.com/gear/wireless-thermometers"
 *   ogImage="https://backyahdbbq.com/images/og-thermometers.jpg"
 * />
 */

import { useEffect } from 'react';
import { getSeoImageUrl } from '@/utils/seoUrl';

export interface SEOHeadProps {
  /** Page title (will be appended with " | Backyahd BBQ" unless isHomepage is true) */
  title: string;

  /** Set to true for homepage to use only "Backyahd BBQ" as title */
  isHomepage?: boolean;

  /** Meta description (150-160 characters recommended) */
  description: string;

  /** Canonical URL for the page */
  canonicalUrl?: string;

  /** Keywords for meta keywords tag (optional, less important for modern SEO) */
  keywords?: string[];

  /** Open Graph type (default: 'website') */
  ogType?: 'website' | 'article' | 'product';

  /** Open Graph image URL (1200x630px recommended) */
  ogImage?: string;

  /** Actual image dimensions; omitted when unknown. */
  ogImageWidth?: number;
  ogImageHeight?: number;

  /** Alt text for Open Graph image */
  ogImageAlt?: string;

  /** Twitter Card type (default: 'summary_large_image') */
  twitterCard?: 'summary' | 'summary_large_image';

  /** Author name for articles */
  author?: string;

  /** Publication date (ISO 8601 format) */
  publishDate?: string;

  /** Last modified date (ISO 8601 format) */
  modifiedDate?: string;

  /** Prevent search engines from indexing this page */
  noindex?: boolean;

  /** Prevent search engines from following links on this page */
  nofollow?: boolean;

  /** Structured data (JSON-LD) */
  structuredData?: object | object[];

  /** Stable identifier used to adopt prerendered JSON-LD during hydration */
  structuredDataId?: string;

  /** Source-backed product brand for product Open Graph metadata */
  productBrand?: string;

  /** Article-specific: reading time */
  readingTime?: string;

  /** Article-specific: section/category */
  section?: string;

  /** Article-specific: tags */
  tags?: string[];
}

const SITE_NAME = 'Backyahd BBQ';
const SITE_URL = 'https://backyahdbbq.com'; // Canonical domain (bkyhd.com redirects here)
const DEFAULT_OG_IMAGE = `${SITE_URL}/images/social-default.png`;
const TWITTER_HANDLE = '@BackyahdBBQ';
const TITLE_MIN_LENGTH = 30;
const TITLE_MAX_LENGTH = 60;
const DESCRIPTION_MIN_LENGTH = 120;
const DESCRIPTION_MAX_LENGTH = 160;
const TITLE_SUFFIX = ` | ${SITE_NAME}`;

function normalizeWhitespace(value: string): string {
  return value.replace(/\s+/g, ' ').trim();
}

function truncateAtWordBoundary(value: string, maxLength: number): string {
  if (value.length <= maxLength) return value;

  const shortened = value.slice(0, maxLength - 3);
  const lastSpace = shortened.lastIndexOf(' ');
  return `${shortened.slice(0, lastSpace > maxLength * 0.6 ? lastSpace : shortened.length).trimEnd()}...`;
}

export function toSeoTitle(
  title: string,
  ogType: SEOHeadProps['ogType'] = 'website',
  isHomepage = false
): string {
  if (isHomepage) return SITE_NAME;

  const context =
    ogType === 'product'
      ? 'Review & Buying Guide'
      : ogType === 'article'
        ? 'BBQ Recipe & Guide'
        : 'BBQ Guide & Reviews';
  const maxBaseLength = TITLE_MAX_LENGTH - TITLE_SUFFIX.length;
  let baseTitle = normalizeWhitespace(title).replace(/\s*\|\s*Backyahd BBQ(?:\s+Recipes)?$/i, '');
  if (!baseTitle) return '';

  if (`${baseTitle}${TITLE_SUFFIX}`.length < TITLE_MIN_LENGTH) {
    baseTitle = `${baseTitle} - ${context}`;
  }

  return `${truncateAtWordBoundary(baseTitle, maxBaseLength)}${TITLE_SUFFIX}`;
}

export function toSeoMetaDescription(
  description: string,
  ogType: SEOHeadProps['ogType'] = 'website'
): string {
  const supplements =
    ogType === 'product'
      ? [
          'See practical product details, related cooks, and buying guidance from Backyahd BBQ.',
          'Compare features and choose the right gear for your next cook.',
        ]
      : ogType === 'article'
        ? [
            'Get clear steps, practical tips, and tested guidance from Backyahd BBQ.',
            'Cook with confidence using advice built for backyard pitmasters.',
          ]
        : [
            'Explore practical BBQ tips, trusted gear recommendations, and tested recipes from Backyahd BBQ.',
            'Find clear guidance for backyard cooks of every skill level.',
          ];
  let result = normalizeWhitespace(description);
  if (!result) return '';

  for (const supplement of supplements) {
    if (result.length >= DESCRIPTION_MIN_LENGTH) break;
    result = `${result}${/[.!?]$/.test(result) ? '' : '.'} ${supplement}`;
  }

  return truncateAtWordBoundary(result, DESCRIPTION_MAX_LENGTH);
}

export function SEOHead({
  title,
  description,
  canonicalUrl,
  keywords = [],
  ogType = 'website',
  ogImage = DEFAULT_OG_IMAGE,
  ogImageAlt,
  ogImageWidth,
  ogImageHeight,
  twitterCard = 'summary_large_image',
  author,
  publishDate,
  modifiedDate,
  noindex = false,
  nofollow = false,
  structuredData,
  structuredDataId,
  productBrand,
  readingTime,
  section,
  tags = [],
  isHomepage = false,
}: SEOHeadProps) {
  const fullTitle = toSeoTitle(title, ogType, isHomepage);
  const metaDescription = toSeoMetaDescription(description, ogType);

  // Ensure canonical URL is absolute
  const absoluteCanonicalUrl = canonicalUrl?.startsWith('http')
    ? canonicalUrl
    : `${SITE_URL}${canonicalUrl || ''}`;

  // Robots meta content
  const robotsContent = [noindex ? 'noindex' : 'index', nofollow ? 'nofollow' : 'follow'].join(
    ', '
  );

  const absoluteOgImage = getSeoImageUrl(ogImage)!;
  const imageWidth = ogImageWidth ?? (absoluteOgImage === DEFAULT_OG_IMAGE ? 1200 : undefined);
  const imageHeight = ogImageHeight ?? (absoluteOgImage === DEFAULT_OG_IMAGE ? 630 : undefined);

  // Direct DOM head management — replaces react-helmet-async (broken with React 19)
  useEffect(() => {
    const originalTitle = document.title;
    const createdTags: Element[] = [];
    const updatedTags: { el: Element; origContent: string | null }[] = [];
    const adoptedJsonLd: HTMLScriptElement[] = [];
    const removedTags: Element[] = [];

    function setImageDimension(key: string, value: number | undefined) {
      if (value && value > 0) setMeta('property', key, String(value));
      else {
        const el = document.head.querySelector(`meta[property="${key}"]`);
        if (el) {
          removedTags.push(el);
          el.remove();
        }
      }
    }

    function setMeta(attr: 'name' | 'property' | 'httpEquiv', key: string, content: string) {
      const selector =
        attr === 'httpEquiv' ? `meta[http-equiv="${key}"]` : `meta[${attr}="${key}"]`;
      let el = document.head.querySelector(selector);
      if (el) {
        // Existing element — update it but track the original value for restoration
        const origContent = el.getAttribute('content');
        el.setAttribute('content', content);
        el.setAttribute('data-seo', 'true');
        updatedTags.push({ el, origContent });
      } else {
        el = document.createElement('meta');
        el.setAttribute(attr === 'httpEquiv' ? 'http-equiv' : attr, key);
        el.setAttribute('content', content);
        el.setAttribute('data-seo', 'true');
        document.head.appendChild(el);
        createdTags.push(el);
      }
    }

    function setLink(rel: string, href: string) {
      // First try to find an existing SEO-managed link, then fall back to any link with this rel
      let el =
        document.head.querySelector(`link[rel="${rel}"][data-seo="true"]`) ||
        document.head.querySelector(`link[rel="${rel}"]`);
      if (el) {
        const origHref = el.getAttribute('href');
        el.setAttribute('href', href);
        el.setAttribute('data-seo', 'true');
        updatedTags.push({ el, origContent: origHref });
      } else {
        el = document.createElement('link');
        el.setAttribute('rel', rel);
        el.setAttribute('data-seo', 'true');
        el.setAttribute('href', href);
        document.head.appendChild(el);
        createdTags.push(el);
      }
    }

    function addJsonLd(data: object | object[], id?: string) {
      const serialized = JSON.stringify(Array.isArray(data) ? data : [data]).replace(
        /</g,
        '\\u003c'
      );
      const existing = id
        ? document.head.querySelector<HTMLScriptElement>(
            `script[type="application/ld+json"][data-seo-jsonld="${id}"]`
          )
        : null;
      if (existing) {
        adoptedJsonLd.push(existing);
        existing.textContent = serialized;
        return;
      }

      // react-check:allow-script -- serialized JSON-LD is assigned through textContent.
      const el = document.createElement('script');
      el.type = 'application/ld+json';
      el.setAttribute('data-seo', 'true');
      if (id) el.setAttribute('data-seo-jsonld', id);
      el.textContent = serialized;
      document.head.appendChild(el);
      createdTags.push(el);
    }

    // Title
    document.title = fullTitle;

    // Primary meta tags
    setMeta('name', 'title', fullTitle);
    setMeta('name', 'description', metaDescription);
    if (keywords.length > 0) setMeta('name', 'keywords', keywords.join(', '));
    setMeta('name', 'robots', robotsContent);
    setMeta('name', 'googlebot', robotsContent);
    if (author) setMeta('name', 'author', author);

    // Canonical URL
    if (canonicalUrl) setLink('canonical', absoluteCanonicalUrl);

    // Open Graph
    setMeta('property', 'og:type', ogType);
    setMeta('property', 'og:url', absoluteCanonicalUrl);
    setMeta('property', 'og:title', fullTitle);
    setMeta('property', 'og:description', metaDescription);
    setMeta('property', 'og:image', absoluteOgImage);
    if (ogImageAlt) setMeta('property', 'og:image:alt', ogImageAlt);
    setImageDimension('og:image:width', imageWidth);
    setImageDimension('og:image:height', imageHeight);
    setMeta('property', 'og:site_name', SITE_NAME);
    setMeta('property', 'og:locale', 'en_US');

    // Article-specific OG
    if (ogType === 'article') {
      if (publishDate) setMeta('property', 'article:published_time', publishDate);
      if (modifiedDate) setMeta('property', 'article:modified_time', modifiedDate);
      if (author) setMeta('property', 'article:author', author);
      if (section) setMeta('property', 'article:section', section);
      tags.forEach((tag) => setMeta('property', 'article:tag', tag));
    }

    // Product-specific OG
    if (ogType === 'product') {
      if (productBrand) setMeta('property', 'product:brand', productBrand);
    }

    // Twitter Card
    setMeta('name', 'twitter:card', twitterCard);
    setMeta('name', 'twitter:url', absoluteCanonicalUrl);
    setMeta('name', 'twitter:title', fullTitle);
    setMeta('name', 'twitter:description', metaDescription);
    setMeta('name', 'twitter:image', absoluteOgImage);
    if (ogImageAlt) setMeta('name', 'twitter:image:alt', ogImageAlt);
    setMeta('name', 'twitter:site', TWITTER_HANDLE);
    if (author) setMeta('name', 'twitter:creator', TWITTER_HANDLE);

    // Additional
    setMeta('name', 'language', 'English');

    // Reading time
    if (readingTime) {
      setMeta('name', 'twitter:label1', 'Reading time');
      setMeta('name', 'twitter:data1', readingTime);
    }

    // Structured Data
    if (structuredData) addJsonLd(structuredData, structuredDataId || absoluteCanonicalUrl);

    // Cleanup: remove elements we created, restore originals we updated
    return () => {
      document.title = originalTitle;
      removedTags.forEach((el) => document.head.appendChild(el));
      createdTags.forEach((el) => {
        if (el.parentNode) el.parentNode.removeChild(el);
      });
      updatedTags.forEach(({ el, origContent }) => {
        if (origContent != null) {
          // <link> elements store value in href; <meta> elements in content
          const attr = el.tagName === 'LINK' ? 'href' : 'content';
          el.setAttribute(attr, origContent);
        }
        el.removeAttribute('data-seo');
      });
      adoptedJsonLd.forEach((el) => {
        if (el.parentNode) el.parentNode.removeChild(el);
      });
    };
  }, [
    fullTitle,
    metaDescription,
    canonicalUrl,
    absoluteCanonicalUrl,
    keywords,
    ogType,
    absoluteOgImage,
    ogImageAlt,
    imageWidth,
    imageHeight,
    twitterCard,
    author,
    publishDate,
    modifiedDate,
    noindex,
    nofollow,
    structuredData,
    structuredDataId,
    productBrand,
    readingTime,
    section,
    tags,
    robotsContent,
  ]);

  return null;
}
/**
 * Default SEO meta tags for pages without specific SEO configuration
 */
export function DefaultSEOHead() {
  return (
    <SEOHead
      title="Low and Slow & Hot and Fast"
      description="Compare 500+ BBQ products, explore 2,200+ posts, and master smoking with expert reviews and recipes from Backyahd BBQ."
      canonicalUrl={SITE_URL}
      keywords={['bbq gear', 'wireless thermometer', 'smoking recipes', 'bbq reviews', 'grilling']}
    />
  );
}
